import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Target, Edit, Trash2, Plus, RotateCcw, Loader2, Save, X, AlertCircle, CheckCircle,
  Table2, Grid3x3, Sparkles, RefreshCw, BookOpen, Download,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---- Schema helpers --------------------------------------------------------
// Pydantic model wants every nested key present on create/update; these helpers
// keep the form small even though the payload is wide.

const EMPTY_TRIO = { p50: 0, p75: 0, p90: 0 };
const EMPTY_PERCENTILES = {
  process: { ...EMPTY_TRIO }, people: { ...EMPTY_TRIO },
  technology: { ...EMPTY_TRIO }, overall: { ...EMPTY_TRIO },
};
const EMPTY_MATURITY = { Initial: 25, Developing: 35, Intermediate: 25, Advanced: 15 };
const EMPTY_KPI = {
  time_to_competency_days_p50: 150,
  productivity_uplift_pct_p50: 10,
  rework_rate_pct_p50: 12,
  innovation_rate_annual_p50: 1.5,
  onboarding_weeks_p50: 10,
  retention_pct_annual_p50: 85,
};

const KPI_LABELS = {
  time_to_competency_days_p50: 'Time-to-competency (days)',
  productivity_uplift_pct_p50: 'Productivity uplift (%)',
  rework_rate_pct_p50:         'Rework rate (%)',
  innovation_rate_annual_p50:  'Innovation rate (init./yr)',
  onboarding_weeks_p50:        'Onboarding (weeks)',
  retention_pct_annual_p50:    'Retention (% annual)',
};

const AdminBenchmarksPage = () => {
  const { token, user } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [options, setOptions] = useState({ industries: [], sizes: [] });
  const [benchmarks, setBenchmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [filterSize, setFilterSize] = useState('all');
  const [viewMode, setViewMode] = useState('table');     // 'table' | 'heatmap'
  const [heatmapPillar, setHeatmapPillar] = useState('overall');  // which pillar the heatmap visualises
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);         // benchmark being edited; null = create mode
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reseeding, setReseeding] = useState(false);
  const [confirmReseed, setConfirmReseed] = useState(false);
  // AI explanation state for the edit dialog
  const [explainPillar, setExplainPillar] = useState('overall');
  const [explainLoading, setExplainLoading] = useState(false);
  const [explainCache, setExplainCache] = useState({});  // keyed by pillar
  // Handbook export state
  const [handbookOpen, setHandbookOpen] = useState(false);
  const [handbookStatus, setHandbookStatus] = useState(null);
  const [handbookPolling, setHandbookPolling] = useState(false);
  // A/B test funnel stats for the "Benchmark My Org" CTA.
  const [abStats, setAbStats] = useState(null);
  const [abLoading, setAbLoading] = useState(false);

  const isAdmin = user?.role === 'admin' || user?.role === 'hr';

  const load = async () => {
    setLoading(true);
    try {
      const [optsR, bmR] = await Promise.all([
        axios.get(`${API}/km-benchmarking/industries`, { headers }),
        axios.get(`${API}/km-benchmarking/benchmarks`, { headers }),
      ]);
      setOptions(optsR.data);
      setBenchmarks(bmR.data.benchmarks || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load benchmarks');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadABStats = async () => {
    setAbLoading(true);
    try {
      const r = await axios.get(`${API}/km-benchmarking/atlas/ab-stats`, { headers });
      setAbStats(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load A/B stats');
    } finally { setAbLoading(false); }
  };

  useEffect(() => {
    if (token) loadABStats();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm({
      industry: options.industries[0]?.value || 'tech',
      company_size: options.sizes[0]?.value || '51-250',
      percentiles: JSON.parse(JSON.stringify(EMPTY_PERCENTILES)),
      maturity_distribution: { ...EMPTY_MATURITY },
      kpi_impact: { ...EMPTY_KPI },
      source: 'Admin-authored',
    });
    setDialogOpen(true);
  };

  const openEdit = (bm) => {
    setEditing(bm);
    setForm({
      industry: bm.industry,
      company_size: bm.company_size,
      percentiles: JSON.parse(JSON.stringify(bm.percentiles || EMPTY_PERCENTILES)),
      maturity_distribution: { ...EMPTY_MATURITY, ...(bm.maturity_distribution || {}) },
      kpi_impact: { ...EMPTY_KPI, ...(bm.kpi_impact || {}) },
      source: bm.source || '',
    });
    // Seed the AI explanation pane with whatever's already cached on the server.
    setExplainCache(bm.ai_explanations || {});
    setExplainPillar(viewMode === 'heatmap' ? heatmapPillar : 'overall');
    setDialogOpen(true);
  };

  const setPercentile = (pillar, tier, v) => {
    const n = Number(v);
    setForm(f => ({
      ...f,
      percentiles: { ...f.percentiles, [pillar]: { ...f.percentiles[pillar], [tier]: Number.isFinite(n) ? n : 0 } },
    }));
  };
  const setMaturity = (k, v) => {
    const n = Number(v);
    setForm(f => ({ ...f, maturity_distribution: { ...f.maturity_distribution, [k]: Number.isFinite(n) ? n : 0 } }));
  };
  const setKpi = (k, v) => {
    const n = Number(v);
    setForm(f => ({ ...f, kpi_impact: { ...f.kpi_impact, [k]: Number.isFinite(n) ? n : 0 } }));
  };

  const maturitySum = form ? Object.values(form.maturity_distribution).reduce((a, b) => a + (Number(b) || 0), 0) : 0;

  const save = async () => {
    if (!form) return;
    // Validate percentile ordering per pillar — catches admin fat-fingers before the server does.
    for (const p of ['process', 'people', 'technology', 'overall']) {
      const trio = form.percentiles[p];
      if (!(trio.p50 <= trio.p75 && trio.p75 <= trio.p90)) {
        toast.error(`${p}: P50 must be ≤ P75 must be ≤ P90`);
        return;
      }
    }
    if (Math.abs(maturitySum - 100) > 0.5) {
      toast.error(`Maturity distribution must sum to 100 (currently ${maturitySum})`);
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/km-benchmarking/benchmarks/${editing.benchmark_id}`, form, { headers });
        toast.success('Benchmark updated');
      } else {
        await axios.post(`${API}/km-benchmarking/benchmarks`, form, { headers });
        toast.success('Benchmark created');
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const remove = async (bm) => {
    if (!window.confirm(`Delete the ${bm.industry} / ${bm.company_size} benchmark? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/km-benchmarking/benchmarks/${bm.benchmark_id}`, { headers });
      toast.success('Benchmark deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const reseed = async () => {
    setReseeding(true);
    try {
      const r = await axios.post(`${API}/km-benchmarking/benchmarks/reseed`, {}, { headers });
      toast.success(`Reseeded ${r.data.seeded} benchmarks`);
      setConfirmReseed(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reseed failed');
    } finally { setReseeding(false); }
  };

  const explainCell = async (refresh = false) => {
    if (!editing) return;
    setExplainLoading(true);
    try {
      const r = await axios.post(
        `${API}/km-benchmarking/benchmarks/${editing.benchmark_id}/explain`,
        null,
        { headers, params: { pillar: explainPillar, refresh } },
      );
      setExplainCache(prev => ({ ...prev, [explainPillar]: r.data.explanation }));
      if (!r.data.cached || refresh) toast.success('AI explanation generated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Explain failed');
    } finally { setExplainLoading(false); }
  };

  // -------- Handbook export --------
  const loadHandbookStatus = async () => {
    try {
      const r = await axios.get(`${API}/km-benchmarking/benchmarks/handbook/prewarm/status`, { headers });
      setHandbookStatus(r.data);
      return r.data;
    } catch { return null; }
  };

  const openHandbook = async () => {
    setHandbookOpen(true);
    await loadHandbookStatus();
  };

  const startPrewarm = async (pillars) => {
    try {
      const r = await axios.post(`${API}/km-benchmarking/benchmarks/handbook/prewarm`, null, {
        headers, params: { pillars: pillars.join(',') },
      });
      if (r.data.already_running) {
        toast.info('A prewarm job is already running');
      } else {
        toast.success('Prewarm started — this may take several minutes');
      }
      setHandbookPolling(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Prewarm failed to start');
    }
  };

  // Poll status every 3s while a prewarm is in flight.
  useEffect(() => {
    if (!handbookPolling) return;
    const id = setInterval(async () => {
      const s = await loadHandbookStatus();
      if (s?.latest_run?.status === 'complete' || s?.latest_run?.status === 'error') {
        setHandbookPolling(false);
        load();
        if (s.latest_run.status === 'complete') toast.success(`Prewarm complete — ${s.latest_run.done} generated`);
      }
    }, 3000);
    return () => clearInterval(id);
  }, [handbookPolling]); // eslint-disable-line react-hooks/exhaustive-deps

  // If a prewarm was already running when the page loaded, pick up polling automatically.
  useEffect(() => {
    if (handbookStatus?.latest_run?.status === 'running' && !handbookPolling) {
      setHandbookPolling(true);
    }
  }, [handbookStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const downloadHandbook = async () => {
    try {
      const r = await axios.get(`${API}/km-benchmarking/benchmarks/handbook.pdf`, {
        headers, responseType: 'blob',
      });
      const today = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `km-benchmark-handbook-${today}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Handbook download failed');
    }
  };

  // ---- Derived view ----
  const filtered = benchmarks.filter(b =>
    (filterIndustry === 'all' || b.industry === filterIndustry) &&
    (filterSize === 'all' || b.company_size === filterSize),
  );
  const ind_label = (v) => options.industries.find(i => i.value === v)?.label || v;
  const size_label = (v) => options.sizes.find(s => s.value === v)?.label || v;

  // Heatmap lookup — fast O(1) access by (industry, size)
  const heatmapIndex = benchmarks.reduce((acc, b) => {
    acc[`${b.industry}|${b.company_size}`] = b;
    return acc;
  }, {});
  // Viridis-ish scale: score 30 → deep red, 50 → amber, 70 → teal, 90 → deep green.
  // Solid backgrounds + white text keep readability at all intensities.
  const colorFor = (score) => {
    if (score == null) return { bg: '#F3F4F6', fg: '#9CA3AF' };
    if (score < 45)  return { bg: '#DC2626', fg: '#FFFFFF' };   // critical
    if (score < 55)  return { bg: '#F97316', fg: '#FFFFFF' };   // weak
    if (score < 65)  return { bg: '#F59E0B', fg: '#0A0A0A' };   // developing
    if (score < 75)  return { bg: '#10B981', fg: '#FFFFFF' };   // good
    if (score < 85)  return { bg: '#059669', fg: '#FFFFFF' };   // strong
    return { bg: '#047857', fg: '#FFFFFF' };                    // top-tier
  };
  const pillarLabel = { overall: 'Overall', process: 'Process', people: 'People', technology: 'Technology' };

  // ---- Access gate ----
  if (!isAdmin && !loading) {
    return (
      <div className="p-8 max-w-2xl mx-auto" data-testid="admin-benchmarks-page">
        <Card className="border-[#FF3B30]/30 bg-[#FFF5F5]">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-8 h-8 text-[#FF3B30] mx-auto mb-2" />
            <p className="text-sm font-semibold text-[#0A0A0A]">Admin access required</p>
            <p className="text-xs text-[#4B5563] mt-1">
              Only admins and HR roles can edit KM benchmarks.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-[1400px] mx-auto" data-testid="admin-benchmarks-page">
      {/* Header */}
      <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A] flex items-center gap-2">
            <Target className="w-6 h-6 text-[#002FA7]" />KM Benchmarks
          </h1>
          <p className="text-sm text-[#4B5563] mt-0.5">
            Curated industry peer norms ({benchmarks.length} total — 8 industries × 4 company sizes).
            Edit any row to override the defaults; reseed to revert everything.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setConfirmReseed(true)}
            disabled={reseeding}
            data-testid="benchmark-reseed-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />Reseed defaults
          </Button>
          <Button
            onClick={openCreate}
            className="bg-[#002FA7] hover:bg-[#00227A]"
            data-testid="benchmark-create-btn"
          >
            <Plus className="w-4 h-4 mr-1.5" />New benchmark
          </Button>
        </div>
      </div>

      {/* A/B Test — "Benchmark My Org" CTA funnel */}
      <Card className="border-[#FF9500]/30 bg-[#FFFBF5] mb-5" data-testid="ab-stats-card">
        <CardContent className="p-4">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
            <div>
              <h3 className="text-sm font-semibold text-[#0A0A0A] flex items-center gap-1.5">
                <Target className="w-4 h-4 text-[#FF9500]" />
                Atlas CTA · A/B test
              </h3>
              <p className="text-xs text-[#6B7280] mt-0.5">
                Conversion funnel from the marketing "Benchmark My Org" button (50/50 random split).
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={loadABStats}
              disabled={abLoading}
              className="text-xs"
              data-testid="ab-stats-refresh-btn"
            >
              {abLoading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </div>
          {!abStats ? (
            <p className="text-xs text-[#6B7280]">Loading stats…</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {abStats.variants.map((v) => {
                const isWinner = (() => {
                  const other = abStats.variants.find(x => x.variant !== v.variant);
                  if (!other) return false;
                  return v.conversion_pct > 0 && v.conversion_pct >= other.conversion_pct;
                })();
                return (
                  <div
                    key={v.variant}
                    className={`rounded-lg border p-3 ${isWinner ? 'border-[#34C759] bg-white' : 'border-[#E5E7EB] bg-white'}`}
                    data-testid={`ab-variant-${v.variant}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={v.variant === 'a' ? 'bg-[#002FA7]' : 'bg-[#AF52DE]'}>
                          Variant {v.variant.toUpperCase()}
                        </Badge>
                        <span className="text-sm font-medium text-[#0A0A0A]">"{v.label}"</span>
                      </div>
                      {isWinner && v.impressions > 0 && (
                        <Badge variant="outline" className="border-[#34C759] text-[#34C759] text-[10px]">Leading</Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <p className="text-xs text-[#6B7280]">Impressions</p>
                        <p className="text-lg font-semibold text-[#0A0A0A]">{v.impressions}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6B7280]">Clicks</p>
                        <p className="text-lg font-semibold text-[#0A0A0A]">{v.clicks}</p>
                        <p className="text-[10px] text-[#9CA3AF]">{v.ctr_pct}% CTR</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#6B7280]">Submissions</p>
                        <p className="text-lg font-semibold text-[#FF9500]">{v.submissions}</p>
                        <p className="text-[10px] text-[#9CA3AF]">{v.conversion_pct}% conv.</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* View toggle + Filters */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="inline-flex rounded-md border border-[#E5E7EB] p-0.5 bg-white">
          <button
            onClick={() => setViewMode('table')}
            className={`px-2.5 py-1.5 rounded text-[11px] font-medium flex items-center gap-1.5 transition-colors ${
              viewMode === 'table' ? 'bg-[#002FA7] text-white' : 'text-[#4B5563] hover:bg-[#F9FAFB]'
            }`}
            data-testid="benchmark-view-table-btn"
          >
            <Table2 className="w-3.5 h-3.5" />Table
          </button>
          <button
            onClick={() => setViewMode('heatmap')}
            className={`px-2.5 py-1.5 rounded text-[11px] font-medium flex items-center gap-1.5 transition-colors ${
              viewMode === 'heatmap' ? 'bg-[#002FA7] text-white' : 'text-[#4B5563] hover:bg-[#F9FAFB]'
            }`}
            data-testid="benchmark-view-heatmap-btn"
          >
            <Grid3x3 className="w-3.5 h-3.5" />Heatmap
          </button>
        </div>

        {viewMode === 'table' && (
          <>
            <Select value={filterIndustry} onValueChange={setFilterIndustry}>
              <SelectTrigger className="w-[240px] h-9 text-xs" data-testid="benchmark-filter-industry">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All industries</SelectItem>
                {options.industries.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterSize} onValueChange={setFilterSize}>
              <SelectTrigger className="w-[240px] h-9 text-xs" data-testid="benchmark-filter-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sizes</SelectItem>
                {options.sizes.map(o => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto text-xs text-[#9CA3AF] self-center">
              Showing {filtered.length} of {benchmarks.length}
            </div>
          </>
        )}

        {viewMode === 'heatmap' && (
          <>
            <Select value={heatmapPillar} onValueChange={setHeatmapPillar}>
              <SelectTrigger className="w-[200px] h-9 text-xs" data-testid="benchmark-heatmap-pillar-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(pillarLabel).map(([k, l]) => (
                  <SelectItem key={k} value={k}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline" size="sm"
              onClick={openHandbook}
              className="h-9 text-xs"
              data-testid="benchmark-handbook-btn"
            >
              <BookOpen className="w-3.5 h-3.5 mr-1.5" />
              Export Handbook
            </Button>
            <div className="ml-auto flex items-center gap-1.5 text-[10px] text-[#4B5563]" data-testid="benchmark-heatmap-legend">
              <span>P50 colour scale:</span>
              {[
                { l: '<45',  c: '#DC2626' },
                { l: '45-54', c: '#F97316' },
                { l: '55-64', c: '#F59E0B' },
                { l: '65-74', c: '#10B981' },
                { l: '75-84', c: '#059669' },
                { l: '85+',   c: '#047857' },
              ].map(s => (
                <span key={s.l} className="flex items-center gap-0.5">
                  <span className="inline-block w-3 h-3 rounded-sm" style={{ background: s.c }} />
                  <span>{s.l}</span>
                </span>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Table */}
      {viewMode === 'table' && (
      <Card className="border-[#E5E7EB]">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#9CA3AF] mx-auto" /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-sm text-[#9CA3AF]">No benchmarks match these filters.</div>
          ) : (
            <Table data-testid="benchmarks-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-[11px]">Industry</TableHead>
                  <TableHead className="text-[11px]">Size</TableHead>
                  <TableHead className="text-[11px] text-right">Overall (P50/P75/P90)</TableHead>
                  <TableHead className="text-[11px] text-right">Process P50</TableHead>
                  <TableHead className="text-[11px] text-right">People P50</TableHead>
                  <TableHead className="text-[11px] text-right">Tech P50</TableHead>
                  <TableHead className="text-[11px]">Source</TableHead>
                  <TableHead className="text-[11px] text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(bm => (
                  <TableRow key={bm.benchmark_id} data-testid={`benchmark-row-${bm.industry}-${bm.company_size}`}>
                    <TableCell className="text-xs">
                      {ind_label(bm.industry)}
                      {bm.is_seed
                        ? <Badge variant="outline" className="ml-1.5 text-[9px] px-1 py-0 h-[14px]">Seed</Badge>
                        : <Badge className="ml-1.5 text-[9px] px-1 py-0 h-[14px] bg-[#FF9500]/10 text-[#FF9500] border border-[#FF9500]/30">Custom</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">{bm.company_size}</TableCell>
                    <TableCell className="text-xs text-right font-mono">
                      {bm.percentiles?.overall?.p50} / {bm.percentiles?.overall?.p75} / {bm.percentiles?.overall?.p90}
                    </TableCell>
                    <TableCell className="text-xs text-right font-mono">{bm.percentiles?.process?.p50}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{bm.percentiles?.people?.p50}</TableCell>
                    <TableCell className="text-xs text-right font-mono">{bm.percentiles?.technology?.p50}</TableCell>
                    <TableCell className="text-[11px] text-[#4B5563] max-w-[240px] truncate" title={bm.source}>{bm.source}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost" size="sm" onClick={() => openEdit(bm)}
                        className="h-7 w-7 p-0"
                        data-testid={`benchmark-edit-btn-${bm.industry}-${bm.company_size}`}
                      >
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost" size="sm" onClick={() => remove(bm)}
                        className="h-7 w-7 p-0 text-[#FF3B30] hover:text-[#FF3B30] hover:bg-[#FF3B30]/10"
                        data-testid={`benchmark-delete-btn-${bm.industry}-${bm.company_size}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}

      {viewMode === 'heatmap' && (
        <Card className="border-[#E5E7EB]" data-testid="benchmark-heatmap">
          <CardContent className="p-4">
            {loading ? (
              <div className="p-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-[#9CA3AF] mx-auto" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-separate" style={{ borderSpacing: '4px' }}>
                  <thead>
                    <tr>
                      <th className="text-[10px] uppercase tracking-wide text-[#9CA3AF] text-left p-2"></th>
                      {options.sizes.map(s => (
                        <th key={s.value} className="text-[10px] uppercase tracking-wide text-[#9CA3AF] text-center p-2">
                          {s.value}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {options.industries.map(ind => (
                      <tr key={ind.value}>
                        <th className="text-xs text-[#0A0A0A] text-right p-2 font-medium whitespace-nowrap">
                          {ind.label}
                        </th>
                        {options.sizes.map(sz => {
                          const bm = heatmapIndex[`${ind.value}|${sz.value}`];
                          const score = bm?.percentiles?.[heatmapPillar]?.p50;
                          const col = colorFor(score);
                          return (
                            <td key={sz.value} className="p-0" data-testid={`heatmap-cell-${ind.value}-${sz.value}`}>
                              <button
                                onClick={() => bm && openEdit(bm)}
                                disabled={!bm}
                                className="w-full min-w-[90px] h-16 rounded-md flex flex-col items-center justify-center transition-transform hover:scale-[1.04] focus:outline-none focus:ring-2 focus:ring-[#002FA7] disabled:cursor-not-allowed"
                                style={{ background: col.bg, color: col.fg }}
                                title={bm
                                  ? `${ind.label} / ${sz.value}\nP50 ${pillarLabel[heatmapPillar]} = ${score}\n${bm.is_seed ? 'Seed' : 'Custom'} · ${bm.source}`
                                  : 'No benchmark'}
                              >
                                <span className="text-lg font-bold leading-none">{score ?? '—'}</span>
                                {bm && !bm.is_seed && (
                                  <span className="text-[9px] mt-0.5 opacity-90">Custom</span>
                                )}
                              </button>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-[10px] text-[#9CA3AF] mt-3">
                  Click any cell to edit the benchmark. Rows are industries, columns are company sizes.
                  Values shown are P50 of <strong>{pillarLabel[heatmapPillar]}</strong>.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="benchmark-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit benchmark' : 'Create benchmark'}</DialogTitle>
            <DialogDescription className="text-xs">
              Percentile values are 0–100. P50 ≤ P75 ≤ P90. Maturity distribution must sum to 100%.
            </DialogDescription>
          </DialogHeader>
          {form && (
            <div className="space-y-4">
              {/* Industry + Size */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Industry</Label>
                  <Select value={form.industry} onValueChange={v => setForm(f => ({ ...f, industry: v }))} disabled={!!editing}>
                    <SelectTrigger data-testid="benchmark-form-industry"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.industries.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {editing && <p className="text-[10px] text-[#9CA3AF] mt-1">Industry+Size pair is immutable once created.</p>}
                </div>
                <div>
                  <Label className="text-xs">Company size</Label>
                  <Select value={form.company_size} onValueChange={v => setForm(f => ({ ...f, company_size: v }))} disabled={!!editing}>
                    <SelectTrigger data-testid="benchmark-form-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.sizes.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Percentiles — 4 pillars × 3 tiers */}
              <div>
                <Label className="text-xs font-semibold">Percentiles</Label>
                <div className="mt-1.5 border border-[#E5E7EB] rounded-md">
                  <table className="w-full text-xs">
                    <thead className="bg-[#FAFAFA] text-[10px] uppercase text-[#9CA3AF]">
                      <tr>
                        <th className="p-2 text-left">Pillar</th>
                        <th className="p-2">P50</th>
                        <th className="p-2">P75</th>
                        <th className="p-2">P90</th>
                      </tr>
                    </thead>
                    <tbody>
                      {['process', 'people', 'technology', 'overall'].map(pillar => (
                        <tr key={pillar} className="border-t border-[#F3F4F6]">
                          <td className="p-2 capitalize font-medium">{pillar}</td>
                          {['p50', 'p75', 'p90'].map(tier => (
                            <td key={tier} className="p-1">
                              <Input
                                type="number" min="0" max="100"
                                value={form.percentiles[pillar][tier]}
                                onChange={e => setPercentile(pillar, tier, e.target.value)}
                                className="h-8 text-xs font-mono w-20 mx-auto"
                                data-testid={`benchmark-form-${pillar}-${tier}`}
                              />
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Maturity distribution */}
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold">Maturity distribution (%)</Label>
                  <span className={`text-[10px] ${Math.abs(maturitySum - 100) < 0.5 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                    {Math.abs(maturitySum - 100) < 0.5 ? <CheckCircle className="w-3 h-3 inline mr-1" /> : <AlertCircle className="w-3 h-3 inline mr-1" />}
                    Sum = {maturitySum.toFixed(0)}% (must be 100)
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-1.5">
                  {Object.keys(EMPTY_MATURITY).map(k => (
                    <div key={k}>
                      <Label className="text-[10px] text-[#4B5563]">{k}</Label>
                      <Input
                        type="number" min="0" max="100"
                        value={form.maturity_distribution[k] ?? 0}
                        onChange={e => setMaturity(k, e.target.value)}
                        className="h-8 text-xs font-mono"
                        data-testid={`benchmark-form-maturity-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* KPI impact */}
              <div>
                <Label className="text-xs font-semibold">KPI impact (P50 baselines)</Label>
                <div className="grid grid-cols-2 gap-2 mt-1.5">
                  {Object.entries(KPI_LABELS).map(([k, label]) => (
                    <div key={k}>
                      <Label className="text-[10px] text-[#4B5563]">{label}</Label>
                      <Input
                        type="number" step="0.1"
                        value={form.kpi_impact[k] ?? 0}
                        onChange={e => setKpi(k, e.target.value)}
                        className="h-8 text-xs font-mono"
                        data-testid={`benchmark-form-kpi-${k}`}
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-xs">Source / citation</Label>
                <Input
                  value={form.source}
                  onChange={e => setForm(f => ({ ...f, source: e.target.value }))}
                  placeholder="e.g. APQC 2025 KM Maturity Study · Q1 recalibration"
                  className="text-xs"
                  data-testid="benchmark-form-source"
                />
              </div>

              {/* AI explanation — only when editing (not creating a brand-new benchmark).
                  Gives admins a grounded 2-3 sentence rationale for WHY this cohort
                  scores the way it does, so they can calibrate their own edits. */}
              {editing && (
                <div
                  className="border border-[#002FA7]/20 rounded-md p-3 bg-[#002FA7]/[0.02]"
                  data-testid="benchmark-explain-panel"
                >
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Label className="text-xs font-semibold flex items-center gap-1.5 m-0">
                      <Sparkles className="w-3.5 h-3.5 text-[#002FA7]" />
                      Explain this benchmark
                      <Badge variant="outline" className="text-[9px] ml-1">GPT-5.2</Badge>
                    </Label>
                    <div className="flex items-center gap-1.5">
                      <Select value={explainPillar} onValueChange={setExplainPillar}>
                        <SelectTrigger className="h-7 text-[11px] w-[130px]" data-testid="benchmark-explain-pillar">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {['overall', 'process', 'people', 'technology'].map(p => (
                            <SelectItem key={p} value={p} className="text-xs capitalize">{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {explainCache[explainPillar] ? (
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => explainCell(true)}
                          disabled={explainLoading}
                          className="h-7 text-[11px] px-2"
                          data-testid="benchmark-explain-refresh-btn"
                          title="Regenerate explanation"
                        >
                          {explainLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => explainCell(false)}
                          disabled={explainLoading}
                          className="h-7 text-[11px] bg-[#002FA7] hover:bg-[#00227A]"
                          data-testid="benchmark-explain-generate-btn"
                        >
                          {explainLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                          Explain
                        </Button>
                      )}
                    </div>
                  </div>
                  {explainCache[explainPillar] ? (
                    <p className="text-[11px] text-[#0A0A0A] leading-relaxed mt-2" data-testid="benchmark-explain-text">
                      {explainCache[explainPillar]}
                    </p>
                  ) : (
                    <p className="text-[11px] text-[#9CA3AF] mt-2 italic">
                      Click <strong>Explain</strong> for a 2-3 sentence rationale anchored in KM research (APQC / Gartner / KM Institute). Results are cached per pillar.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving} data-testid="benchmark-form-cancel-btn">
              <X className="w-4 h-4 mr-1.5" />Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="bg-[#002FA7] hover:bg-[#00227A]" data-testid="benchmark-form-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              {editing ? 'Save changes' : 'Create benchmark'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Handbook export dialog — shows coverage stats, prewarm trigger, and download button */}
      <Dialog open={handbookOpen} onOpenChange={setHandbookOpen}>
        <DialogContent className="max-w-lg" data-testid="benchmark-handbook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#002FA7]" />
              KM Benchmark Handbook
            </DialogTitle>
            <DialogDescription className="text-xs">
              Compile every benchmark with its AI-generated rationale into a single PDF.
              Admins use this to onboard new team members or calibrate their own norms.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {/* Coverage stats */}
            {handbookStatus && (
              <div className="p-3 rounded-md border border-[#E5E7EB] bg-[#FAFAFA]" data-testid="benchmark-handbook-coverage">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold">AI coverage</span>
                  <span className="text-xs text-[#0A0A0A]">
                    <strong>{handbookStatus.coverage?.cached ?? 0}</strong>
                    <span className="text-[#9CA3AF]">/{handbookStatus.coverage?.total ?? 0} explanations</span>
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      {handbookStatus.coverage?.pct ?? 0}%
                    </Badge>
                  </span>
                </div>
                {/* Progress bar */}
                <div className="mt-2 h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#002FA7] transition-all"
                    style={{ width: `${handbookStatus.coverage?.pct ?? 0}%` }}
                  />
                </div>
                <p className="text-[10px] text-[#9CA3AF] mt-1.5">
                  32 benchmarks × 4 pillars = 128 possible explanations. Missing pillars render as placeholders in the PDF.
                </p>
              </div>
            )}

            {/* Active prewarm */}
            {handbookStatus?.latest_run?.status === 'running' && (
              <div className="p-3 rounded-md border border-[#002FA7]/30 bg-[#002FA7]/[0.04]" data-testid="benchmark-handbook-progress">
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin text-[#002FA7]" />
                  <div className="flex-1">
                    <div className="text-xs font-semibold text-[#0A0A0A]">Prewarming explanations…</div>
                    <div className="text-[11px] text-[#4B5563]">
                      {handbookStatus.latest_run.done} / {handbookStatus.latest_run.total} done
                      {handbookStatus.latest_run.failed > 0 && (
                        <span className="text-[#FF9500] ml-2">· {handbookStatus.latest_run.failed} failed</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="mt-2 h-1 bg-[#E5E7EB] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#002FA7] transition-all"
                    style={{
                      width: handbookStatus.latest_run.total > 0
                        ? `${(handbookStatus.latest_run.done / handbookStatus.latest_run.total) * 100}%`
                        : '0%',
                    }}
                  />
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => startPrewarm(['overall', 'process', 'people', 'technology'])}
                disabled={handbookStatus?.latest_run?.status === 'running'}
                className="h-10"
                data-testid="benchmark-handbook-prewarm-btn"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                Prewarm all missing
              </Button>
              <Button
                onClick={downloadHandbook}
                className="h-10 bg-[#002FA7] hover:bg-[#00227A]"
                data-testid="benchmark-handbook-download-btn"
              >
                <Download className="w-4 h-4 mr-1.5" />
                Download PDF
              </Button>
            </div>
            <p className="text-[10px] text-[#9CA3AF]">
              Prewarm runs in the background (~5s per explanation). You can close this dialog and come back —
              progress is preserved. Already-cached explanations are skipped.
            </p>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setHandbookOpen(false)} data-testid="benchmark-handbook-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm reseed dialog — separate from delete since this wipes custom edits */}
      <Dialog open={confirmReseed} onOpenChange={setConfirmReseed}>
        <DialogContent className="max-w-md" data-testid="benchmark-reseed-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-4 h-4 text-[#FF9500]" />
              Reseed all benchmarks?
            </DialogTitle>
            <DialogDescription className="text-xs">
              This will <strong>delete every benchmark</strong> — including any you've customised —
              and replace them with the factory defaults (32 rows, APQC/KM-Institute composite).
              Reports already generated are unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmReseed(false)} disabled={reseeding} data-testid="benchmark-reseed-cancel-btn">
              Cancel
            </Button>
            <Button onClick={reseed} disabled={reseeding} className="bg-[#FF9500] hover:bg-[#E08500]" data-testid="benchmark-reseed-confirm-btn">
              {reseeding ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
              Yes, reseed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBenchmarksPage;
